import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Form, Row } from 'react-bootstrap';
import type { SkillBundle, SkillBundleFile } from '../../apps/apis/claudeCodeDashboard/types';

export interface ReviewableSkillAsset {
  assetId: string;
  adapter: string;
  contentMarkdown: string;
  contentHash: string;
  targetPath: string;
  bundle?: SkillBundle;
}

interface Props {
  assets: ReviewableSkillAsset[];
  title: string;
  previousAssets?: ReviewableSkillAsset[];
}

interface ReviewFile extends SkillBundleFile {
  adapter: string;
  deleted?: boolean;
}

const reviewFileKey = (file: Pick<ReviewFile, 'adapter' | 'path'>): string => `${file.adapter}:${file.path}`;
const MAX_LCS_MATRIX_CELLS_BEFORE_UI_FREEZE = 250_000;

const filesForReview = (asset: ReviewableSkillAsset): ReviewFile[] => {
  if (asset.bundle) {
    return asset.bundle.files.map((file) => ({ ...file, adapter: file.adapter ?? asset.adapter }));
  }
  return [
    {
      path: asset.targetPath,
      mediaType: 'text/markdown',
      byteSize: new TextEncoder().encode(asset.contentMarkdown).length,
      contentHash: asset.contentHash,
      content: asset.contentMarkdown,
      adapter: asset.adapter,
    },
  ];
};

const diffText = (previous: string | undefined, current: string): string => {
  if (previous === undefined || previous === current) {
    return current;
  }
  const previousLines = previous.split('\n');
  const currentLines = current.split('\n');
  if (previousLines.length * currentLines.length > MAX_LCS_MATRIX_CELLS_BEFORE_UI_FREEZE) {
    return [...previousLines.map((line) => `- ${line}`), ...currentLines.map((line) => `+ ${line}`)].join('\n');
  }
  const longestCommonSubsequence = Array.from({ length: previousLines.length + 1 }, () =>
    Array<number>(currentLines.length + 1).fill(0),
  );
  for (let previousIndex = previousLines.length - 1; previousIndex >= 0; previousIndex -= 1) {
    for (let currentIndex = currentLines.length - 1; currentIndex >= 0; currentIndex -= 1) {
      longestCommonSubsequence[previousIndex]![currentIndex] =
        previousLines[previousIndex] === currentLines[currentIndex]
          ? (longestCommonSubsequence[previousIndex + 1]![currentIndex + 1] ?? 0) + 1
          : Math.max(
              longestCommonSubsequence[previousIndex + 1]![currentIndex] ?? 0,
              longestCommonSubsequence[previousIndex]![currentIndex + 1] ?? 0,
            );
    }
  }

  const lines: string[] = [];
  let previousIndex = 0;
  let currentIndex = 0;
  while (previousIndex < previousLines.length || currentIndex < currentLines.length) {
    if (previousLines[previousIndex] === currentLines[currentIndex]) {
      lines.push(`  ${previousLines[previousIndex]}`);
      previousIndex += 1;
      currentIndex += 1;
    } else if (
      currentIndex === currentLines.length ||
      (previousIndex < previousLines.length &&
        (longestCommonSubsequence[previousIndex + 1]![currentIndex] ?? 0) >=
          (longestCommonSubsequence[previousIndex]![currentIndex + 1] ?? 0))
    ) {
      lines.push(`- ${previousLines[previousIndex]}`);
      previousIndex += 1;
    } else {
      lines.push(`+ ${currentLines[currentIndex]}`);
      currentIndex += 1;
    }
  }
  return lines.join('\n');
};

/** Displays untrusted skill files as inert source text; never render a Markdown/HTML preview. */
const SkillBundleReviewPanel = ({ assets, title, previousAssets = [] }: Props) => {
  const currentFiles = useMemo(() => assets.flatMap(filesForReview), [assets]);
  const previousFiles = useMemo(() => previousAssets.flatMap(filesForReview), [previousAssets]);
  const files = useMemo(() => {
    const currentKeys = new Set(currentFiles.map(reviewFileKey));
    return [
      ...currentFiles,
      ...previousFiles
        .filter((file) => !currentKeys.has(reviewFileKey(file)))
        .map((file) => ({ ...file, deleted: true })),
    ];
  }, [currentFiles, previousFiles]);
  const [selectedFileKey, setSelectedFileKey] = useState('');

  useEffect(() => {
    setSelectedFileKey((currentFileKey) =>
      files.some((file) => reviewFileKey(file) === currentFileKey)
        ? currentFileKey
        : files[0]
          ? reviewFileKey(files[0])
          : '',
    );
  }, [files]);

  const selected = files.find((file) => reviewFileKey(file) === selectedFileKey) ?? files[0];
  const previous = selected
    ? previousFiles.find((file) => file.path === selected.path && file.adapter === selected.adapter)
    : undefined;

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <div>
          <div className="fw-semibold">{title}</div>
          <div className="text-muted small">
            Escaped source and text-only diff; media, HTML, SVG, and links never execute.
          </div>
        </div>
        <Badge bg="secondary">
          {files.length} file{files.length === 1 ? '' : 's'}
        </Badge>
      </Card.Header>
      <Card.Body>
        {assets.length === 0 ? (
          <Alert variant="warning" className="mb-0">
            No reviewable bundle asset is available.
          </Alert>
        ) : (
          <>
            <Row className="g-2 small mb-3">
              {assets.map((asset) => (
                <Col md={6} key={asset.assetId}>
                  <div className="border rounded p-2 h-100">
                    <div className="text-muted">Adapter</div>
                    <div>{asset.adapter}</div>
                    <div className="text-muted mt-2">Bundle root hash</div>
                    <div className="text-break font-monospace">{asset.bundle?.rootHash ?? asset.contentHash}</div>
                  </div>
                </Col>
              ))}
            </Row>
            <Form.Group controlId="skill-bundle-file" className="mb-3">
              <Form.Label className="small text-muted">Bundle file</Form.Label>
              <Form.Select
                value={selected ? reviewFileKey(selected) : ''}
                onChange={(event) => setSelectedFileKey(event.target.value)}
              >
                {files.map((file) => (
                  <option key={reviewFileKey(file)} value={reviewFileKey(file)}>
                    {file.adapter} · {file.path}
                    {file.deleted ? ' · deleted' : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {selected ? (
              <>
                <Row className="g-2 small mb-3">
                  <Col md={4}>
                    <span className="text-muted">Media type: </span>
                    {selected.mediaType}
                  </Col>
                  <Col md={4}>
                    <span className="text-muted">Bytes: </span>
                    {selected.byteSize.toLocaleString()}
                  </Col>
                  <Col md={4} className="text-break">
                    <span className="text-muted">SHA-256: </span>
                    <span className="font-monospace">{selected.contentHash}</span>
                  </Col>
                </Row>
                <div className="mb-2 small text-muted">
                  {selected.deleted
                    ? 'Deleted from current bundle'
                    : previous
                      ? 'Text diff against matching previous file'
                      : 'Escaped source'}
                </div>
                <pre className="border rounded p-3 mb-0 bg-light text-wrap">
                  <code>{diffText(previous?.content, selected.deleted ? '' : selected.content)}</code>
                </pre>
              </>
            ) : null}
            <Alert variant="light" className="border small mt-3 mb-0">
              Reviewer checklist: inspect every adapter and file, compare root and per-file hashes, confirm risk
              findings and lineage, then verify that conversion creates a draft before any separate publish action.
            </Alert>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SkillBundleReviewPanel;
